import { sendEventPackage } from '@/api/api';
import { IDeviceInfo, IEvent, IPage, OSType, VAction } from '@/interfaces/event-log';
import clientStore from '@/store/client-store';
import eventTrackStore from '@/store/event-track';
import moment from 'moment';
import bridge from './bridge';

const packageConfig = require('../../package.json');

declare global {
  interface Window {
    __DEBUG_EVENT__?: boolean;
  }
}

class EventPackage {
  private events: Array<IEvent> = [];
  private device: IDeviceInfo | null = null;
  private transactionId: string | undefined = '';
  getEvents() {
    return this.events;
  }
  setEvents(value: Array<IEvent>) {
    this.events = value;
  }
  pushEvent(value: IEvent) {
    this.events.push(value);
  }
  clearEvents() {
    this.events = [];
  }
  getDevice() {
    return this.device;
  }
  setDevice(value: IDeviceInfo) {
    this.device = { ...this.device, ...value };
  }
  getTransactionId() {
    return this.transactionId;
  }
  setTransactionId(id: string | undefined) {
    this.transactionId = id;
  }
}

class EventTrack {
  public eventPackage = new EventPackage();
  private currentPage: IPage | null = null;

  public init() {
    if (process.env.MODE === 'production') {
      window.__DEBUG_EVENT__ = false;
    } else {
      window.__DEBUG_EVENT__ = true;
    }
    this.getCachedEvents();
  }

  public setCurrentPage(page: IPage) {
    this.currentPage = page;
  }

  public pushEvent(event: IEvent) {
    if (!clientStore.getAccessToken() && event.action === VAction.PAGE_EVENT) {
      console.log('no access token, no page event');
      return;
    }
    if (eventTrackStore.ifEventSent()) {
      console.log('already sent the events');
      return;
    }
    if (!event.startTime) {
      event.startTime = moment.now();
    }
    event.location = this.currentPage || undefined;
    this.log(event);
    if (bridge.isWebView()) {
      // call bridge function
      bridge.trackEvent(event);
    } else {
      try {
        this.eventPackage.pushEvent(event);
        eventTrackStore.updateCashedEvents(this.eventPackage.getEvents());
      } catch (e) {
        this.clearEvents();
      }
    }
  }

  public async flushEvents() {
    console.log('begin flush');
    // no need to send the events in webview
    if (bridge.isWebView()) return;
    console.log('sent events', eventTrackStore.ifEventSent());

    if (eventTrackStore.ifEventSent() || !this.eventPackage.getEvents().length) return;
    eventTrackStore.updateEventsSent(true);
    try {
      console.log('clear events');
      const result = JSON.stringify(this.eventPackage);
      this.clearEvents();
      console.log('send events');
      await sendEventPackage(JSON.parse(result));
    } catch (e) {
      console.log('flush events error', e);
    }
  }

  public clearEvents = () => {
    this.eventPackage.clearEvents();
    eventTrackStore.clearEventsData();
  };

  public resetAll = () => {
    this.eventPackage.clearEvents();
    eventTrackStore.clearData();
  };

  public initUserIdAndTrxId = (userId: string | undefined, trxId: string | undefined) => {
    this.eventPackage.setDevice({
      userId: userId,
    });
    this.eventPackage.setTransactionId(trxId);
  };

  get enabled() {
    return window.__DEBUG_EVENT__ === true;
  }

  private getCachedEvents() {
    try {
      this.eventPackage.setEvents(eventTrackStore.getValue().cachedEvents);
      this.initDeviceInfo();
      this.eventPackage.setTransactionId(clientStore.getTransactionId());
    } catch (e) {
      console.log('get cachedEvents failed');
      this.clearEvents();
    }
  }

  private initDeviceInfo() {
    this.eventPackage.setDevice({
      deviceId: eventTrackStore.getDeviceId(),
      osType: OSType.WEB,
      userId: clientStore.getAccessToken(),
      appVersion: packageConfig.version,
    });
  }

  private log(event: IEvent) {
    if (this.enabled) {
      const { action, location, target, status, extra, startTime, endTime } = event;
      console.table({
        action,
        startTime,
        endTime,
        location: location?.pageName,
        locationParams: location?.params,
        target: target?.pageName,
        targetParams: target?.params,
        ...status,
        extra,
        ...this.eventPackage.getDevice(),
        transactionId: this.eventPackage.getTransactionId(),
      });
    }
  }
}

export const eventTrack = new EventTrack();
